import { useEffect, useMemo } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { ChevronRight, CircleUser, X } from "lucide-react";
import { useForm } from "react-hook-form";

import { RmButton } from "@/components/RmButton/RmButton";
import {
  RmCollapsible,
  RmCollapsibleContent,
  RmCollapsibleTrigger,
} from "@/components/RmCollapsible";
import { RmForm } from "@/components/RmForm";
import { RmSelectInput } from "@/components/RmSelectInput/RmSelectInput";
import {
  RmSidebar,
  RmSidebarContent,
  RmSidebarFooter,
  RmSidebarHeader,
  RmSidebarInset,
  RmSidebarMenu,
  RmSidebarMenuButton,
  RmSidebarMenuItem,
  RmSidebarMenuSub,
  RmSidebarMenuSubButton,
  RmSidebarMenuSubItem,
} from "@/components/RmSidebar";
import { RmText } from "@/components/RmText/RmText";
import { Can } from "@/context/AuthorizationCanContext";
import { sidebar } from "@/domain/sidebar";
import { useCurrentStaff } from "@/hooks/useCurrentStaff";
import { useSidebar } from "@/hooks/useSidebar";
import { useTranslator } from "@/hooks/useTranslate";
import { userCurrentCampusId, userRoleAtom } from "@/stores/atoms";
import { hasPermission } from "@/utils/authorization";
import { cn } from "@/utils/tailwind";

import { FullLogo } from "./FullLogo";

export const AppSidebar = () => {
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslator();

  const currentStaff = useCurrentStaff();
  const currentUserRole = useAtomValue(userRoleAtom);
  const [currentCampusId, setCurrentCampusId] = useAtom(userCurrentCampusId);

  const form = useForm({ defaultValues: { campusId: currentCampusId ?? "" } });

  // Keep form value in sync with the atom value
  useEffect(() => {
    form.setValue("campusId", currentCampusId ?? "");
  }, [currentCampusId, form]);

  const campusesOptions = useMemo(() => {
    const options = [{ value: "", label: "All Campuses" }];

    const sortedCampuses = currentStaff?.campuses
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name));

    sortedCampuses?.forEach(campus =>
      options.push({
        value: campus.id,
        label: `(${campus.short_code}) ${campus.name}`,
      }),
    );

    return options;
  }, [currentStaff]);

  const campusId = form.watch("campusId");

  useEffect(() => {
    setCurrentCampusId(campusId ?? null);
  }, [campusId, setCurrentCampusId]);

  return (
    <RmSidebar>
      <RmSidebarHeader className="flex flex-row items-center justify-between gap-2">
        <FullLogo className="mr-7 h-12 md:mx-7" />
        <RmSidebarInset className="md:hidden">
          <RmButton size="icon" variant="outline" onClick={toggleSidebar}>
            <X />
          </RmButton>
        </RmSidebarInset>
      </RmSidebarHeader>
      <RmSidebarContent className="no-scrollbar px-2">
        <RmSidebarMenu className="gap-0 py-2">
          <RmForm form={form} isInputOnlyForm>
            <RmSelectInput
              className="mb-2 justify-between"
              name="campusId"
              control={form.control}
              options={campusesOptions}
              isClearable={false}
            />
          </RmForm>
          {sidebar.map(item =>
            item.isGroup ? (
              (item.types?.some(type =>
                hasPermission({
                  userId: currentStaff?.id ?? null,
                  userRole: currentUserRole,
                  action: "list",
                  subject: type,
                  campusId: currentCampusId,
                }),
              ) ?? true) ? (
                <RmCollapsible
                  key={item.title}
                  defaultOpen
                  className={cn("group/collapsible")}>
                  <RmSidebarMenuItem>
                    <RmCollapsibleTrigger asChild>
                      <div className="flex">
                        <RmSidebarMenuButton>
                          {item.icon}
                          <RmText as="span">{t(item.title)}</RmText>
                          <div className="flex flex-1 items-end justify-end">
                            <ChevronRight className="size-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                          </div>
                        </RmSidebarMenuButton>
                      </div>
                    </RmCollapsibleTrigger>
                    <RmCollapsibleContent className="py-0">
                      <RmSidebarMenuSub className="ml-2 gap-1">
                        {item.children?.map(child => (
                          <Can
                            key={child.title}
                            I="list"
                            a={child.type ?? "staff"}>
                            <RmSidebarMenuSubItem className="flex py-0">
                              <Link className="flex flex-1" to={child.url}>
                                <RmSidebarMenuSubButton
                                  isActive={location.pathname === child.url}>
                                  {t(child.title)}
                                </RmSidebarMenuSubButton>
                              </Link>
                            </RmSidebarMenuSubItem>
                          </Can>
                        ))}
                      </RmSidebarMenuSub>
                    </RmCollapsibleContent>
                  </RmSidebarMenuItem>
                </RmCollapsible>
              ) : null
            ) : (
              <Can key={item.title} I="list" a={item.types?.[0] ?? "staff"}>
                <RmSidebarMenuItem className="py-0">
                  <Link to={item.url} className="px-0">
                    <RmSidebarMenuButton
                      isActive={location.pathname === item.url}>
                      {item.icon}
                      {t(item.title)}
                    </RmSidebarMenuButton>
                  </Link>
                </RmSidebarMenuItem>
              </Can>
            ),
          )}
        </RmSidebarMenu>
      </RmSidebarContent>
      <RmSidebarFooter>
        <Link to="/admin/account">
          <RmSidebarMenuButton
            isActive={location.pathname === "/admin/account"}>
            <CircleUser className="mr-2" />
            {t("common:account")}
          </RmSidebarMenuButton>
        </Link>
      </RmSidebarFooter>
    </RmSidebar>
  );
};
